import React, { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import { Button } from "../Form";
import { HiCheckCircle, HiOutlineCheckCircle } from "react-icons/hi";
import { FiCalendar, FiMail, FiPhone } from "react-icons/fi";
import { BsGenderAmbiguous } from "react-icons/bs";
import { RiFileGifFill } from "react-icons/ri";
import { LuCalendarDays } from "react-icons/lu";
import moment from "moment";
import { toast } from "react-hot-toast";
import bookingApi from "../../api/booking/BookingApi";
import Loading from "../../Util/Loading";

function CustomerModal({ closeModal, isOpen, datas }) {
  console.log({ datas });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [renderBookings, setRenderBookings] = useState(false);

  const userId = datas?.userId;
  const bookingIds = datas?.bookingIds;

  // Fetch bookings from backend
  useEffect(() => {
    const fetchBookings = async () => {
      if (!userId || !Array.isArray(bookingIds) || bookingIds.length === 0) {
        setBookings([]);
        setLoading(false);
        return;
      }

      try {
        const { response, err } = await bookingApi.getBookingsByUserAndIds({
          userId,
          bookingIds,
        });

        if (err) throw err;
        setBookings(response.data);
      } catch (error) {
        console.error("Failed to load bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [userId, bookingIds]);

  const user = bookings[0]?.user || {};

  // Safari-safe custom date parser
  function parseSelectedDateTime(dateStr) {
    const [datePart, timePart, ampm] = dateStr.split(" ");
    if (!datePart || !timePart || !ampm) return null;

    const [hourStr, minuteStr] = timePart.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;

    const dateTimeISO = `${datePart}T${String(hour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}:00`;
    const parsed = new Date(dateTimeISO);
    return isNaN(parsed) ? null : parsed;
  }

  // Filter bookings
  const filteredBookings = useMemo(() => {
    if (!Array.isArray(bookings)) return [];

    const now = Date.now();

    return bookings
      .filter((b) => {
        const date = parseSelectedDateTime(b.selectedDateTime);
        if (!date) return false;

        if (tab === "upcoming") return date.getTime() > now;
        if (tab === "completed") return date.getTime() <= now;
        return true;
      })
      .sort(
        (a, b) =>
          parseSelectedDateTime(a.selectedDateTime) -
          parseSelectedDateTime(b.selectedDateTime)
      );
  }, [bookings, tab]);

  // Lazy rendering
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setRenderBookings(true), 100);
      return () => clearTimeout(timer);
    } else {
      setRenderBookings(false);
    }
  }, [isOpen]);

  const now = Date.now();

  const bookingCounts = useMemo(() => {
    let upcoming = 0;
    let completed = 0;

    for (let b of bookings) {
      const date = parseSelectedDateTime(b.selectedDateTime);
      if (!date) continue;

      if (date.getTime() > now) {
        upcoming++;
      } else {
        completed++;
      }
    }

    return {
      all: bookings.length,
      upcoming,
      completed,
    };
  }, [bookings]);

  if (loading) return <Loading />;

  return (
    <Modal
      closeModal={closeModal}
      isOpen={isOpen}
      title="Customer Details"
      width="max-w-3xl"
    >
      <div className="flex-colo gap-6 w-full text-white">
        {/* Header */}
        <div className="w-full p-6 rounded-xl bg-slate-800 shadow border border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-yellowlight pb-4">
            <div className="flex items-start gap-4">
              <img
                src={user.photo}
                alt={user.displayName}
                className="w-16 h-16 rounded-full object-cover border border-yellowlight"
              />
              <div className="flex flex-col">
                <h2 className="text-xl font-semibold">{user.displayName}</h2>
                <div className="text-sm text-slate-300 flex items-center gap-2">
                  <FiPhone className="text-yellowlight" />
                  {user.phone}
                </div>
                <div className="text-sm text-slate-300 flex items-center gap-2">
                  <FiMail className="text-yellowlight" />
                  {user.username}
                </div>
                <div className="text-sm text-slate-300 flex items-center gap-2">
                  <FiCalendar className="text-yellowlight" />
                  Created{" "}
                  {moment(user.createdAt || bookings[0]?.createdAt).format(
                    "D MMM YYYY"
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 text-sm text-slate-300">
              <div className="flex items-center gap-3">
                <BsGenderAmbiguous className="text-xl" />
                {user.gender ? (
                  <span className="text-white capitalize">
                    {user.gender.toLowerCase()}
                  </span>
                ) : (
                  <span className="text-slate-400">Add pronouns</span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <RiFileGifFill className="text-xl" />
                {user.dateOfBirth ? (
                  <span className="text-white">
                    {moment(user.dateOfBirth).format("D MMM YYYY")}
                  </span>
                ) : (
                  <span className="text-slate-400">Add date of birth</span>
                )}
              </div>

              {user.bio && (
                <p className="text-sm italic text-gray-400 mt-1 line-clamp-3">
                  “{user.bio}”
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 border-b border-border pb-2 mt-6">
          {["all", "upcoming", "completed"].map((type) => (
            <button
              key={type}
              onClick={() => setTab(type)}
              className={`px-4 py-1 text-sm rounded-full capitalize ${
                tab === type
                  ? "bg-yellowlight text-black font-semibold"
                  : "bg-slate-700 text-white"
              }`}
            >
              {type}{" "}
              <span className="text-xs ml-1">({bookingCounts[type]})</span>
            </button>
          ))}
        </div>

        {/* Booking Timeline */}
        {renderBookings ? (
          filteredBookings.length > 0 ? (
            <div className="w-full space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {filteredBookings.map((booking, i) => {
                const services = [
                  ...(Array.isArray(booking.selectedNailServices)
                    ? booking.selectedNailServices
                    : []),
                  ...(Array.isArray(booking.selectedBeautyServices)
                    ? booking.selectedBeautyServices
                    : []),
                ];
                const total = services.reduce(
                  (sum, s) => sum + parseFloat(s?.price || 0),
                  0
                );

                const isFuture =
                  parseSelectedDateTime(booking.selectedDateTime).getTime() >
                  Date.now();

                const staff = booking.selectedProfessional._id || {};

                return (
                  <div key={booking._id} className="flex gap-4 items-start">
                    <div
                      className={`rounded-full p-2 transition-all duration-300 ${
                        isFuture
                          ? "bg-yellowlight text-yellowdark"
                          : "bg-green-600 text-white"
                      }`}
                    >
                      {isFuture ? (
                        <LuCalendarDays className="w-5 h-5" />
                      ) : (
                        <HiCheckCircle className="w-5 h-5" />
                      )}
                    </div>

                    <div
                      className={`p-4 rounded-xl shadow w-full transition-colors duration-300 ${
                        isFuture
                          ? "bg-slate-800 border border-border"
                          : "bg-slate-900 border border-slate-700"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className={`text-sm text-gray-400`}>
                            {new Date(
                              parseSelectedDateTime(booking.selectedDateTime)
                            ).toLocaleDateString("en-NZ", {
                              weekday: "long",
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </p>
                          <h3 className="text-base font-semibold text-white">
                            Appointment
                          </h3>
                          <span
                            className={`text-sm ${
                              isFuture ? "text-yellowlight" : "text-green-500"
                            }`}
                          >
                            {isFuture ? "Booked" : "Completed"}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1 mt-2 text-sm">
                        {services.map((srv, idx) => (
                          <div
                            key={idx}
                            className={`flex justify-between ${
                              isFuture ? "text-white" : "text-gray-400"
                            }`}
                          >
                            <span>
                              {idx + 1}. {srv.title}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <img
                          src={staff.photo}
                          alt={staff.name}
                          className="w-6 h-6 rounded-full object-cover border border-yellowlight"
                        />
                        <span className="text-sm text-textGray font-medium">
                          {staff.name}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-400 italic">
              No bookings in this category.
            </div>
          )
        ) : (
          <div className="text-sm text-slate-400 italic animate-pulse">
            Loading bookings...
          </div>
        )}
      </div>
    </Modal>
  );
}

export default CustomerModal;
